import React from 'react';

import { AddressTypes, useResponsive } from '@sg/garnish';

import { OrderStatus } from '@order/features/order-status';

import {
  AppFooter,
  ChallengeCompletionModal,
  LoadingAnimation,
  NotFoundView,
  SignedOutView,
} from '../../components';
import { useIsLoggedIn, useIsLoggingIn } from '../../context/AuthMachine';
import { useDeliveryOrderInFlight } from '../../hooks';
import {
  OrderStatusContactUs,
  OrderStatusCtas,
  OrderStatusDetails,
  OrderStatusInfo,
  OrderStatusMap,
} from './components';
import { useLateOrderNoticeBanner, useOrderStatusData } from './hooks';

export const OrderStatusScreen = () => {
  const isLoggedIn = useIsLoggedIn();
  const isLoggingIn = useIsLoggingIn();

  // ─────────────────────────────────────────────────────────────────

  if (isLoggingIn) return <LoadingAnimation />;

  if (!isLoggedIn) return <SignedOutView />;

  return <OrderStatusScreenSignedIn />;
};

const OrderStatusScreenSignedIn = () => {
  const data = useOrderStatusData();

  useDeliveryOrderInFlight(); // results later used from cache.

  const { isRenderedOnScreen, isFetching, isInvalidOrderStatus, celebration } = data;

  if (isInvalidOrderStatus) return <NotFoundView />;

  if (isFetching) return <LoadingAnimation />;

  return (
    <>
      <OrderStatusScreenReady {...data} />

      {celebration && isRenderedOnScreen ? (
        <ChallengeCompletionModal challengeCompletionDetails={celebration} />
      ) : null}
    </>
  );
};

const OrderStatusScreenReady = (props: ReturnType<typeof useOrderStatusData>) => {
  const {
    reorderOrder,
    hasDeliveryAddress,
    deliveryAddressName,
    deliveryAddressStreet,
    deliveryAddressSecondaryStreet,
    deliveryAddressCity,
    deliveryAddressState,
    deliveryAddressZipCode,
    deliveryAddressNotes,
    deliveryAddressType,
    deliveryAddressLatitude,
    deliveryAddressLongitude,
    deliveryEstimatedTime,
    deliveryPreference,
    locationName,
    locationAddress,
    locationCity,
    locationState,
    locationZipCode,
    locationLatitude,
    locationLongitude,
    dropoffLocation,
    orderId,
    orderType,
    orderStatus,
    orderTotal,
    orderWantedTime,
    orderLedger,
    orderLineItems,
    payments,
    isLate,
    isPast,
    isPendingFeedback = false,
    isTrackable = false,
    orderToHealthKitLineItems,
    isOrderToHealthKitEnabled,
    vendorId,
    canCancel,
    customerName,
    customerLatitude,
    customerLongitude,
    courierName,
    courierPhone,
    courierLatitude,
    courierLongitude,
    restaurantId,
    momoSurveyUrl,
    onReorder,
    refetchOrderStatus,
  } = props;

  const { currentBreakpoint } = useResponsive();

  const isInStoreOrder = orderType === AddressTypes['in-store'];
  const isOrderCancelled = orderStatus === 'failed';
  const canReorder = !isInStoreOrder && (isOrderCancelled || isPast);

  const isInStoreOrderRateable = Boolean(momoSurveyUrl);
  const canRateDigitalOrInStore = isPendingFeedback || isInStoreOrderRateable;
  const canRateOrder = !isOrderCancelled && isPast && canRateDigitalOrInStore;

  useLateOrderNoticeBanner({ isLate });

  if (currentBreakpoint.isXS) {
    return (
      <>
        <OrderStatus.Layout.ContainerWrapper>
          <OrderStatus.Layout.Container>
            <OrderStatus.Layout.Info>
              <OrderStatusInfo
                orderId={orderId}
                vendorId={vendorId}
                orderType={orderType}
                orderStatus={orderStatus}
                wantedTime={orderWantedTime}
                estimatedDeliveryTime={deliveryEstimatedTime}
                orderTotal={orderTotal}
                locationName={locationName}
                locationAddress={locationAddress}
                isTrackable={isTrackable}
                isPendingFeedback={canRateOrder}
                momoSurveyUrl={momoSurveyUrl}
                canReorder={canReorder}
                isPast={isPast}
                onReorder={onReorder}
              />
            </OrderStatus.Layout.Info>

            <OrderStatus.Layout.Map>
              <OrderStatusMap
                orderType={orderType}
                orderStatus={orderStatus}
                locationName={locationName}
                locationLatitude={locationLatitude}
                locationLongitude={locationLongitude}
                customerName={customerName}
                customerLatitude={customerLatitude}
                customerLongitude={customerLongitude}
                hasDeliveryAddress={hasDeliveryAddress}
                deliveryAddressName={deliveryAddressName}
                deliveryAddressType={deliveryAddressType}
                deliveryAddressLatitude={deliveryAddressLatitude}
                deliveryAddressLongitude={deliveryAddressLongitude}
                courierName={courierName}
                courierLatitude={courierLatitude}
                courierLongitude={courierLongitude}
              />
            </OrderStatus.Layout.Map>

            <OrderStatus.Layout.Content>
              <OrderStatusDetails
                order={reorderOrder}
                orderId={orderId}
                orderType={orderType}
                orderStatus={orderStatus}
                payments={payments}
                isOrderCancelled={isOrderCancelled}
                hasDeliveryAddress={hasDeliveryAddress}
                deliveryAddressName={deliveryAddressName}
                deliveryAddressStreet={deliveryAddressStreet}
                deliveryAddressSecondaryStreet={deliveryAddressSecondaryStreet}
                deliveryAddressCity={deliveryAddressCity}
                deliveryAddressState={deliveryAddressState}
                deliveryAddressZipCode={deliveryAddressZipCode}
                deliveryAddressNotes={deliveryAddressNotes}
                deliveryPreference={deliveryPreference}
                locationName={locationName}
                locationAddress={locationAddress}
                locationCity={locationCity}
                locationState={locationState}
                locationZipCode={locationZipCode}
                lineItems={orderLineItems}
                dropoffLocation={dropoffLocation}
                ledger={orderLedger}
                courierName={courierName}
                courierPhone={courierPhone}
                isOrderHealthKitEnabled={isOrderToHealthKitEnabled}
                orderToHealthKitLineItems={orderToHealthKitLineItems}
                orderWantedTime={orderWantedTime}
              />

              <OrderStatusContactUs
                orderId={orderId}
                canCancel={canCancel}
                restaurantId={restaurantId}
                refetchOrderStatus={refetchOrderStatus}
              />
            </OrderStatus.Layout.Content>
          </OrderStatus.Layout.Container>
        </OrderStatus.Layout.ContainerWrapper>

        {canReorder || canRateOrder ? (
          <OrderStatus.Layout.Footer>
            <OrderStatusCtas
              orderId={orderId}
              orderTotal={orderTotal}
              isPendingFeedback={canRateOrder}
              momoSurveyUrl={momoSurveyUrl}
              canReorder={canReorder}
              onReorder={onReorder}
            />
          </OrderStatus.Layout.Footer>
        ) : null}
      </>
    );
  }

  return (
    <OrderStatus.Layout.ContainerWrapper>
      <OrderStatus.Layout.Container>
        <OrderStatus.Layout.Map>
          <OrderStatusMap
            orderType={orderType}
            orderStatus={orderStatus}
            locationName={locationName}
            locationLatitude={locationLatitude}
            locationLongitude={locationLongitude}
            customerName={customerName}
            customerLatitude={customerLatitude}
            customerLongitude={customerLongitude}
            hasDeliveryAddress={hasDeliveryAddress}
            deliveryAddressName={deliveryAddressName}
            deliveryAddressType={deliveryAddressType}
            deliveryAddressLatitude={deliveryAddressLatitude}
            deliveryAddressLongitude={deliveryAddressLongitude}
            courierName={courierName}
            courierLatitude={courierLatitude}
            courierLongitude={courierLongitude}
          />
        </OrderStatus.Layout.Map>

        <OrderStatus.Layout.SidePanel>
          <OrderStatus.Layout.Info>
            <OrderStatusInfo
              orderId={orderId}
              vendorId={vendorId}
              orderType={orderType}
              orderStatus={orderStatus}
              wantedTime={orderWantedTime}
              estimatedDeliveryTime={deliveryEstimatedTime}
              orderTotal={orderTotal}
              locationName={locationName}
              locationAddress={locationAddress}
              isTrackable={isTrackable}
              isPendingFeedback={canRateOrder}
              momoSurveyUrl={momoSurveyUrl}
              canReorder={canReorder}
              isPast={isPast}
              onReorder={onReorder}
            />
          </OrderStatus.Layout.Info>

          <OrderStatus.Layout.Content>
            <OrderStatusDetails
              order={reorderOrder}
              orderId={orderId}
              orderType={orderType}
              orderStatus={orderStatus}
              payments={payments}
              isOrderCancelled={isOrderCancelled}
              hasDeliveryAddress={hasDeliveryAddress}
              deliveryAddressName={deliveryAddressName}
              deliveryAddressStreet={deliveryAddressStreet}
              deliveryAddressSecondaryStreet={deliveryAddressSecondaryStreet}
              deliveryAddressCity={deliveryAddressCity}
              deliveryAddressState={deliveryAddressState}
              deliveryAddressZipCode={deliveryAddressZipCode}
              deliveryAddressNotes={deliveryAddressNotes}
              deliveryPreference={deliveryPreference}
              locationName={locationName}
              locationAddress={locationAddress}
              locationCity={locationCity}
              locationState={locationState}
              locationZipCode={locationZipCode}
              lineItems={orderLineItems}
              dropoffLocation={dropoffLocation}
              ledger={orderLedger}
              courierName={courierName}
              courierPhone={courierPhone}
              isOrderHealthKitEnabled={isOrderToHealthKitEnabled}
              orderToHealthKitLineItems={orderToHealthKitLineItems}
              orderWantedTime={orderWantedTime}
            />

            <OrderStatusContactUs
              orderId={orderId}
              canCancel={canCancel}
              restaurantId={restaurantId}
              refetchOrderStatus={refetchOrderStatus}
            />
          </OrderStatus.Layout.Content>
        </OrderStatus.Layout.SidePanel>
      </OrderStatus.Layout.Container>

      <AppFooter />
    </OrderStatus.Layout.ContainerWrapper>
  );
};
